import { useEffect, useRef } from "react";
import { AnimatePresence, m } from "framer-motion";

export default function Modal({ open, onClose, titleId = "modal-title", children }) {
  const panelRef = useRef(null);
  const lastActiveRef = useRef(null);

  // Lock scroll when open
  // useEffect(() => {
  //   if (!open) return;
  //   const prevOverflow = document.documentElement.style.overflow;
  //   document.documentElement.style.overflow = "hidden";
  //   return () => (document.documentElement.style.overflow = prevOverflow);
  // }, [open]);

  // Return focus to the last trigger when closing
  useEffect(() => {
    if (open) {
      lastActiveRef.current = document.activeElement;
    } else if (lastActiveRef.current) {
      lastActiveRef.current.focus?.();
    }
  }, [open]);

  // Focus trap + ESC close
  useEffect(() => {
    if (!open) return;
    const panel = panelRef.current;
    if (!panel) return;

    const focusables = () =>
      Array.from(
        panel.querySelectorAll(
          'a[href], button, textarea, input, select, [tabindex]:not([tabindex="-1"])'
        )
      ).filter((el) => !el.hasAttribute("disabled") && !el.getAttribute("aria-hidden"));

    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose();
      }
      if (e.key === "Tab") {
        const els = focusables();
        if (els.length === 0) return;
        const first = els[0];
        const last = els[els.length - 1];
        const active = document.activeElement;

        if (e.shiftKey && active === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && active === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    // Focus first focusable on open
    const els = focusables();
    (els[0] || panel).focus();

    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  return (
    <AnimatePresence>
      {open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
        >
          {/* Backdrop */}
          <m.button
            type="button"
            className="absolute inset-0 bg-black/40"
            onClick={onClose}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            aria-label="Close modal"
          />

          {/* Panel */}
          <m.div
            ref={panelRef}
            className="relative z-10 w-[min(92vw,720px)] rounded-2xl bg-white p-6 shadow-2xl focus:outline-none"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
            transition={{ duration: 0.2 }}
            tabIndex={-1}
          >
            {/* Close (top-right) */}
            <button
              onClick={onClose}
              className="absolute right-3 top-3 z-50 rounded-md px-2 py-1 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
            >
              ✕ <span className="sr-only">Close</span>
            </button>

            {children}
          </m.div>
        </div>
      )}
    </AnimatePresence>
  );
}
